// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Valida token ao carregar
  useEffect(() => {
    if (!token) {
      setValidatingToken(false);
      setTokenValid(false);
      setError('Token não fornecido. Por favor, use o link do email.');
      return;
    }

    // Por enquanto, assumimos que o token é válido
    // A validação real será feita no backend ao resetar
    setValidatingToken(false);
    setTokenValid(true);
  }, [token]);

  // Calcula força da senha
  const calculatePasswordStrength = (pwd) => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 8) strength += 1;
    if (pwd.length >= 12) strength += 1;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 1;
    if (/[0-9]/.test(pwd)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 1;
    return strength;
  };

  // Validação de senha forte
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) {
      errors.push('A senha deve ter no mínimo 8 caracteres');
    }
    if (!/[a-zA-Z]/.test(pwd)) {
      errors.push('A senha deve conter pelo menos uma letra');
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('A senha deve conter pelo menos um número');
    }
    return errors;
  };

  // Handler de mudança de senha
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(calculatePasswordStrength(newPassword));
    if (fieldErrors.password) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.password;
        return newErrors;
      });
    }
    if (error && error.includes('senha')) {
      setError('');
    }
  };

  // Handler de mudança de confirmação
  const handlePasswordConfirmationChange = (e) => {
    setPasswordConfirmation(e.target.value);
    if (fieldErrors.passwordConfirmation) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.passwordConfirmation;
        return newErrors;
      });
    }
    if (error && error.includes('senhas')) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    try {
      // Validação client-side
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        setFieldErrors({ password: passwordErrors.join(', ') });
        setError(passwordErrors.join(', '));
        setLoading(false);
        return;
      }

      if (password !== passwordConfirmation) {
        setFieldErrors({ passwordConfirmation: 'As senhas não coincidem' });
        setError('As senhas não coincidem');
        setLoading(false);
        return;
      }

      // Envia requisição
      await api.post('/auth/reset-password', {
        token,
        password,
        password_confirmation: passwordConfirmation
      });

      // Mostra sucesso e redireciona após 3 segundos
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao redefinir senha';
      setError(errorMessage);
      
      // Se o token for inválido ou expirado, mostra opção de solicitar novo
      if (errorMessage.includes('Token') || errorMessage.includes('token') || errorMessage.includes('expirado')) {
        setTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="auth-container">
        <div className="auth-form-side">
          <div className="auth-form-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid || success) {
    return (
      <div className="auth-container">
        {/* Hero Side */}
        <div className="auth-hero">
          <div className="auth-hero-content">
            <div className="auth-logo">H</div>
            <h1>Haklai Church</h1>
            <p>{success ? 'Sua senha foi redefinida com sucesso!' : 'Link inválido ou expirado'}</p>
          </div>
        </div>

        {/* Form Side */}
        <div className="auth-form-side">
          <div className="auth-form-container">
            {success ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  background: 'var(--success-light)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: 'var(--success)' }}>
                    check_circle
                  </span>
                </div>
                <h3 style={{ marginBottom: '0.5rem' }}>Senha Redefinida!</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                  Sua senha foi redefinida com sucesso. Você será redirecionado para o login em instantes...
                </p>
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => navigate('/login')}
                >
                  Ir para Login
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  background: 'var(--danger-light)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: 'var(--danger)' }}>
                    error
                  </span>
                </div>
                <h3 style={{ marginBottom: '0.5rem' }}>Token Inválido ou Expirado</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                  O link de recuperação de senha é inválido ou já expirou. 
                  <br />
                  Por favor, solicite um novo link.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Solicitar Novo Link
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/login')}
                  >
                    Voltar ao Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      {/* Hero Side */}
      <div className="auth-hero">
        <div className="auth-hero-content">
          <div className="auth-logo">H</div>
          <h1>Haklai Church</h1>
          <p>Defina uma nova senha para sua conta.</p>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side">
        <div className="auth-form-container">
          {/* Header */}
          <div className="auth-form-header">
            <h2>Redefinir Senha</h2>
            <p>Digite sua nova senha abaixo</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            <div className="form-group form-group-password">
              <label className="form-label">Nova Senha</label>
              <input
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${fieldErrors.password ? 'error' : ''}`}
                placeholder="Mínimo 8 caracteres, letras e números"
                value={password}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                minLength={8}
                autoFocus
              />
              <span
                className="password-toggle material-symbols-outlined"
                onClick={() => setShowPassword(!showPassword)}
                style={{ cursor: 'pointer' }}
              >
                {showPassword ? 'visibility' : 'visibility_off'}
              </span>
              {fieldErrors.password && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {fieldErrors.password}
                </span>
              )}
              {password && !fieldErrors.password && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem' }}>
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          background: passwordStrength >= level 
                            ? passwordStrength <= 2 
                              ? 'var(--danger)' 
                              : passwordStrength <= 4 
                                ? 'var(--warning)' 
                                : 'var(--success)'
                            : 'var(--border)',
                          transition: 'all 0.2s ease'
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {passwordStrength <= 2 
                      ? 'Senha fraca' 
                      : passwordStrength <= 4 
                        ? 'Senha média' 
                        : 'Senha forte'}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group form-group-password" style={{ marginTop: '1rem' }}>
              <label className="form-label">Confirmar Nova Senha</label>
              <input
                type={showPasswordConfirmation ? 'text' : 'password'}
                className={`form-input ${fieldErrors.passwordConfirmation ? 'error' : ''}`}
                placeholder="Repita a senha"
                value={passwordConfirmation}
                onChange={handlePasswordConfirmationChange}
                required
                disabled={loading}
                minLength={8}
              />
              <span
                className="password-toggle material-symbols-outlined"
                onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                style={{ cursor: 'pointer' }}
              >
                {showPasswordConfirmation ? 'visibility' : 'visibility_off'}
              </span>
              {fieldErrors.passwordConfirmation && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {fieldErrors.passwordConfirmation}
                </span>
              )}
              {passwordConfirmation && password !== passwordConfirmation && !fieldErrors.passwordConfirmation && (
                <span style={{ color: 'var(--warning)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  As senhas não coincidem
                </span>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg auth-submit"
              disabled={loading || !password || !passwordConfirmation}
            >
              {loading ? (
                <>
                  <span className="material-symbols-outlined" style={{ animation: 'spin 1s linear infinite' }}>
                    refresh
                  </span>
                  Redefinindo...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">lock_reset</span>
                  Redefinir Senha
                </>
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <a 
                href="#/login" 
                onClick={(e) => { e.preventDefault(); navigate('/login'); }}
                style={{ color: 'var(--primary)', textDecoration: 'none' }}
              >
                ← Voltar ao Login
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
